import { useResetPasswordMutation, useSignInMutation, useSignUpMutation } from '@/src/api/rtkQuery/userApi'
import { useAuthForm } from '@/src/hooks/useAuthForm'
import { refreshAuthUser, setAuthUser } from '@/src/redux/slices/authSlice'
import { useAppDispatch } from '@/src/redux/store'
import { invokeCustomEvent } from '@/src/utils/analytics/clevertap'
import { constructSignUpPayload, processAuthResponse } from '@/src/utils/authUtils'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ContactsIcon from '@mui/icons-material/Contacts'
import LuggageIcon from '@mui/icons-material/Luggage'
import PercentIcon from '@mui/icons-material/Percent'
import { useTranslations } from 'next-intl'
import React, { useCallback, useState, useEffect, useRef } from 'react'
import { SignInModal } from './SignInModal'

export interface SignInModalContainerProps {
    open: boolean
    onClose: () => void
    onMenuClose?: () => void
}

const benefits = [
    {
        text: 'specialOffers',
        icon: <PercentIcon fontSize="small" />
    },
    {
        text: 'addTravellerInfo',
        icon: <ContactsIcon fontSize="small" />
    },
    {
        text: 'quickCheckout',
        icon: <LuggageIcon fontSize="small" />
    },
    {
        text: 'manageBooking',
        icon: <AssignmentIcon fontSize="small" />
    },
    {
        text: 'immediateRefund',
        icon: <AccountBalanceWalletIcon fontSize="small" />
    }
]

const SignInModalContainer: React.FC<SignInModalContainerProps> = ({ open, onClose, onMenuClose }) => {
    const dispatch = useAppDispatch()
    const [tab, setTab] = useState(0)
    const [forgot, setForgot] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)

    // Track setTimeout ID for cleanup
    const googleSignInTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const handleClose = useCallback(() => {
        setError(null)
        setSuccess(null)
        setForgot(false)
        onClose()
    }, [onClose])

    // Handle successful Google Sign-In
    const handleGoogleSignIn = useCallback(
        async (userData: {
            email: string
            firstName: string
            lastName: string
            fullName: string
            picture?: string
            googleId: string
            userId?: string | number
            ctAuth?: string
        }) => {
            try {
                setLoading(true)
                setError(null)
                setSuccess(null)

                console.log('Google Sign-In userData received:', userData)

                // Create auth data object
                const authData = {
                    signedIn: true,
                    email: userData.email,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    fullName: userData.fullName,
                    picture: userData.picture,
                    googleId: userData.googleId,
                    userId: userData.userId,
                    ctAuth: userData.ctAuth
                }

                // Update the auth state with the user data
                dispatch(setAuthUser(authData))

                setSuccess('Successfully signed in with Google!')

                // Close the modal first
                handleClose()
                // Reset menu state to prevent it from opening automatically
                onMenuClose?.()

                // Give time for cookies to be set by the backend before refreshing
                // The backend sets cookies via Set-Cookie headers with credentials: 'include'
                if (googleSignInTimeoutRef.current) {
                    clearTimeout(googleSignInTimeoutRef.current)
                    googleSignInTimeoutRef.current = null
                }
                googleSignInTimeoutRef.current = setTimeout(() => {
                    console.log('Refreshing auth state from cookies...')
                    dispatch(refreshAuthUser())
                    googleSignInTimeoutRef.current = null
                }, 500)
            } catch (error) {
                console.error('Error handling Google Sign-In:', error)
                setError('Failed to sign in with Google. Please try again.')
            } finally {
                setLoading(false)
            }
        },
        [dispatch, handleClose, onMenuClose]
    )

    // useAuthForm custom hook logic
    const { formState, handlers, validators } = useAuthForm()

    // RTK Query mutations
    const [signIn] = useSignInMutation()
    const [signUp] = useSignUpMutation()
    const [resetPassword] = useResetPasswordMutation()

    // Tab change handler
    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTab(newValue)
        setForgot(false)
        setError(null)
        setSuccess(null)
    }

    // Sign In logic
    const handleSignIn = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        setLoading(true)
        setError(null)
        setSuccess(null)

        // Validate form
        if (!validators.validateEmail() || !validators.validatePassword()) {
            setError('Please enter valid email and password.')
            setLoading(false)
            return
        }

        try {
            const payload = {
                username: formState.email,
                password: formState.password
            }

            const response = await signIn(payload).unwrap()

            // Process the auth response and update Redux store
            const authData = processAuthResponse(response, dispatch, formState.email)

            if (authData) {
                invokeCustomEvent('CLEVERTAP_PROFILE', { type: 'login' })
                setSuccess('Signed in successfully!')
                // Close the modal first
                handleClose()
                // Reset menu state to prevent it from opening automatically
                onMenuClose?.()
            } else {
                setError('Invalid email or password.')
            }
        } catch (error) {
            setError('Something went wrong. Please try again.')
            console.error('Sign in error:', error)
        } finally {
            setLoading(false)
        }
    }

    // Sign Up logic
    const handleSignUp = async () => {
        setLoading(true)
        setError(null)
        setSuccess(null)

        // Validate form
        if (
            !validators.validateEmail() ||
            !validators.validatePassword() ||
            !validators.validateFirstName() ||
            !validators.validateLastName()
        ) {
            setError('Please fill all required fields.')
            setLoading(false)
            return
        }

        try {
            // Construct the signup payload using the helper function
            const payload = constructSignUpPayload({
                email: formState.email,
                password: formState.password,
                firstName: formState.firstName,
                lastName: formState.lastName,
                title: formState.title,
                marketingSubscription: formState.travelCheckedStatus
            })

            const response = await signUp(payload).unwrap()

            // Check if signup was successful
            if (response?.status === 'SUCCESS' && response?.userUniqueId) {
                setSuccess('Account created! Signing you in...')

                // Automatically sign in the user with the same credentials
                try {
                    const signInPayload = {
                        username: formState.email,
                        password: formState.password
                    }

                    const signInResponse = await signIn(signInPayload).unwrap()

                    // Process the auth response and update Redux store
                    const authData = processAuthResponse(signInResponse, dispatch, formState.email)

                    if (authData) {
                        invokeCustomEvent('CLEVERTAP_PROFILE', { type: 'login' })
                        setSuccess('Successfully signed up and signed in!')
                        // Close the modal first
                        handleClose()
                        // Reset menu state to prevent it from opening automatically
                        onMenuClose?.()
                    } else {
                        setError('Failed to sign in after signup. Please try signing in manually.')
                    }
                } catch (signInError) {
                    console.error('Auto sign-in after signup failed:', signInError)
                    setError('Account created successfully! Please sign in manually.')
                }
            } else if (response?.status === 'EXISITING USER' || response?.statusCode === 403) {
                setError('An account with this email already exists. Please sign in to continue.')
                setTab(0)
                handlers.setPassword('')
            } else {
                setError(response?.msg?.[0] || 'Signup failed. Please try again.')
            }
        } catch (error) {
            setError('Something went wrong. Please try again.')
            console.error('Sign up error:', error)
        } finally {
            setLoading(false)
        }
    }

    // Forgot Password logic
    const handleForgotPassword = async () => {
        setLoading(true)
        setError(null)
        setSuccess(null)
        const emailValid = validators.validateEmail()
        if (!emailValid) {
            setLoading(false)
            setError('Please enter a valid email.')
            return
        }
        try {
            const res: any = await resetPassword({ email: formState.email }).unwrap()
            // Assume a property like res.success or res.message indicates success
            if (res?.success || res?.message) {
                setSuccess('Password reset email sent!')
            } else {
                setError('Email not found or could not send reset email.')
            }
        } catch (e) {
            setError('Something went wrong. Please try again.')
        }
        setLoading(false)
    }

    const handleForgotClick = () => {
        setForgot(true)
        setError(null)
        setSuccess(null)
    }

    const handleBackToSignIn = () => {
        setForgot(false)
        setError(null)
        setSuccess(null)
    }

    // Grouped props
    const form = formState
    const status = { loading, error, success, tab, forgot }
    const modalHandlers = {
        onTabChange: handleTabChange,
        onSignIn: handleSignIn,
        onSignUp: handleSignUp,
        onForgotPassword: handleForgotPassword,
        onForgotClick: handleForgotClick,
        onBackToSignIn: handleBackToSignIn,
        onEmailChange: handlers.changeEmailHandler,
        onPasswordChange: handlers.changePasswordHandler,
        onSubmit: handleSignIn,
        setRememberCheckedStatus: handlers.setRememberCheckedStatus,
        handlers
    }

    const t = useTranslations('signInModal')

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (googleSignInTimeoutRef.current) {
                clearTimeout(googleSignInTimeoutRef.current)
                googleSignInTimeoutRef.current = null
            }
        }
    }, [])

    return (
        <SignInModal
            open={open}
            onClose={handleClose}
            form={form}
            status={status}
            handlers={modalHandlers}
            benefits={benefits}
            t={t}
            onGoogleSignIn={handleGoogleSignIn}
        />
    )
}

export { SignInModalContainer }
export default SignInModalContainer
